import type { ApolloError } from '@apollo/client';
import { useToast } from '@hover/blueprint';
import type { GraphQLError } from 'graphql';
import { useHistory, useLocation } from 'react-router-dom';

import { isSsoError } from 'src/modules/onboarding/components/Login/Component';
import type {
  OnboardingCreateHomeownerUserMutation,
  OnboardingCreateUserMutation,
} from 'src/shared/graphql/generated';
import {
  useOnboardingCreateHomeownerUserMutation,
  useOnboardingCreateUserMutation,
} from 'src/shared/graphql/generated';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { useSendCustomHubSpotEvent } from 'src/shared/hooks/hubspot';
import { useGlobalStorageContext } from 'src/shared/hooks/useGlobalStorage';
import { Route as Routes } from 'src/shared/route';
import { Page, accountCreated } from 'src/shared/telemetry';
import { logError } from 'src/shared/telemetry/logger';

// TODO: Add unit test for this hook
export const useOnboardingMagicLink = ({
  isEmailAndSignUpOptionsCombined = false,
  redirectToUrl,
}: {
  isEmailAndSignUpOptionsCombined?: boolean;
  redirectToUrl?: string;
}) => {
  const history = useHistory();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const redirectTo = searchParams.get('redirect_to');

  const t = useTranslate(TranslationNamespace.SHARED);
  const toast = useToast();
  const { homeownerAppIsUserHomeowner: isHomeownerSignup } =
    useGlobalStorageContext();
  const sendHubSpotEvent = useSendCustomHubSpotEvent();
  const [createUser] = useOnboardingCreateUserMutation();
  const [createHomeownerUser] = useOnboardingCreateHomeownerUserMutation();

  const handleOnClickMagicLink = async (email?: string) => {
    if (!isEmailAndSignUpOptionsCombined && !!redirectToUrl) {
      history.push(redirectToUrl);
    } else if (email) {
      try {
        if (isHomeownerSignup) {
          await createHomeownerUser({
            variables: {
              args: {
                user: {
                  email,
                },
                withMagicLink: true,
                redirectTo: Routes.HOMEOWNER_APP_HOME.path,
              },
            },
            onCompleted: ({
              onboardingCreateHomeownerUser,
            }: OnboardingCreateHomeownerUserMutation) =>
              accountCreated({
                button_or_link: 'button',
                button_or_link_text: t('onboarding.acceptAndContinue'),
                page_or_screen_name: Page.SIGN_UP_ONBOARDING,
                primary_cta: true,
                platform_type: 'web',
                user_id: onboardingCreateHomeownerUser?.user?.id,
              }),
          });
        } else {
          await createUser({
            variables: {
              createUserArgs: {
                user: {
                  email,
                },
                withMagicLink: true,
                redirectTo,
              },
            },
            onCompleted: ({
              onboardingCreateUser,
            }: OnboardingCreateUserMutation) =>
              accountCreated({
                button_or_link: 'button',
                button_or_link_text: t('onboarding.acceptAndContinue'),
                page_or_screen_name: Page.SIGN_UP_ONBOARDING,
                primary_cta: true,
                platform_type: 'web',
                user_id: onboardingCreateUser?.user?.id,
              }),
          });
        }
        await sendHubSpotEvent({
          email,
          eventName: 'pe23226235_sign-up-event',
        });
      } catch (e) {
        const error = e as ApolloError;
        const graphQLErrors = error.graphQLErrors as GraphQLError[];
        if (graphQLErrors) {
          if (isSsoError(graphQLErrors)) {
            history.push(`${Routes.SIGN_UP_ONBOARDING.path}/login/sso`);
            return;
          }
          graphQLErrors.forEach(gqlError => {
            toast({
              description: gqlError.message,
              status: 'error',
              duration: 5000,
              isClosable: true,
              containerStyle: {
                maxWidth: '100%',
                whiteSpace: 'pre-wrap',
              },
            });
          });
        }
        logError(e as Error);
        return;
      }

      history.push(`${Routes.SIGN_UP_ONBOARDING.path}/check`);
    }
  };

  return { handleOnClickMagicLink };
};
